import { DAEMON_URL } from "../config";
import { HttpError } from "./errors";

/**
 * GET the built BP+RP artifact zip for a project@branch as an ArrayBuffer.
 * Reuses the session-cookie auth (credentials:"include"). Throws HttpError on
 * non-2xx so callers can branch on .status:
 *   401 unauthorized · 403 forbidden · 404 unknown project / branch not found
 *   422 build failed · 504 build timeout
 * On error the daemon returns a JSON { error, message, stderr? } body; the
 * thrown HttpError.message carries `message` (or `error`) for display.
 *
 * @param branch  leave empty ("") to let the daemon use the project's default branch.
 */
export async function fetchArtifact(project: string, branch: string): Promise<ArrayBuffer> {
  const qs = new URLSearchParams({ project });
  if (branch.trim()) qs.set("branch", branch.trim());
  const url = `${DAEMON_URL}/deploy/artifact?${qs.toString()}`;

  let res: Response;
  try {
    res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/zip" },
      credentials: "include",
    });
  } catch (e) {
    throw new Error(`network error: ${(e as Error).message}`);
  }

  if (res.status === 401) throw new HttpError(401, "unauthorized");
  if (res.status === 403) throw new HttpError(403, "forbidden: no access to this project");
  if (!res.ok) {
    let msg = `http ${res.status}`;
    try {
      const j = (await res.clone().json()) as { error?: string; message?: string };
      msg = j.message || j.error || msg;
    } catch {
      // body was not JSON
    }
    throw new HttpError(res.status, msg);
  }

  return res.arrayBuffer();
}

// ===== Pull-based deploy (helper polls the daemon) =====

export type DeployJobStatus = "building" | "ready" | "delivered" | "done" | "error";

export interface DeployStatus {
  status: DeployJobStatus;
  folder: string | null;
  commitSha: string | null;
  fileCount: number | null;
  error: string | null;
}

/** Queue a deploy job for the helper paired with `code`. Returns the job id. */
export async function dispatchDeploy(code: string, project: string, branch: string): Promise<string> {
  let res: Response;
  try {
    res = await fetch(`${DAEMON_URL}/deploy/dispatch`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ code, project, branch: branch.trim() }),
    });
  } catch (e) {
    throw new Error(`network error: ${(e as Error).message}`);
  }
  if (res.status === 401) throw new HttpError(401, "unauthorized");
  if (res.status === 403) throw new HttpError(403, "forbidden: no access to this project");
  if (!res.ok) {
    let msg = `http ${res.status}`;
    try { const j = (await res.json()) as { error?: string; message?: string }; msg = j.message || j.error || msg; } catch { /* */ }
    throw new HttpError(res.status, msg);
  }
  const j = (await res.json()) as { jobId: string };
  return j.jobId;
}

export async function getDeployStatus(jobId: string): Promise<DeployStatus> {
  let res: Response;
  try {
    res = await fetch(`${DAEMON_URL}/deploy/status?jobId=${encodeURIComponent(jobId)}`, { credentials: "include" });
  } catch (e) {
    throw new Error(`network error: ${(e as Error).message}`);
  }
  if (res.status === 401) throw new HttpError(401, "unauthorized");
  if (!res.ok) throw new HttpError(res.status, `http ${res.status}`);
  return (await res.json()) as DeployStatus;
}
